import {
  Button,
  Col,
  Row,
  Form as BForm,
  InputGroup,
  Tabs,
  Tab
} from '@themesberg/react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import React, { useEffect } from 'react'
import Preloader from '../../../components/Preloader'
import useEditEmailTemplate from './hooks/useEditEmailTemplate'
import { emailTemplateSchema } from './templateSchema'
import Trigger from '../../../components/OverlayTrigger'
import { DeleteConfirmationModal, GalleryModal } from '../../../components/ConfirmationModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImages } from '@fortawesome/free-regular-svg-icons'
import EditEmailTemplate from '../../../components/EditEmailTemplate'
import { getTenantLanguagesStart } from '../../../store/redux-slices/bonus'
import { toast } from '../../../components/Toast'
import NotificationIconContainer from '../../../components/NotificationIcon/NotificationIconContainer'
import PageBackButton from '../../../components/PageBackButton'


// Email Template Form Skeleton Component
const EmailTemplateFormSkeleton = () => {
  // Inline styles for skeleton elements
  const skeletonStyle = (width, height = '18px') => ({
    width: width || '120px',
    height: height,
    backgroundColor: '#e9ecef',
    borderRadius: '4px',
    display: 'inline-block',
    animation: 'pulse 1.5s infinite ease-in-out'
  })

  return (
    <div className="FormsBg">
      <Row className="mt-3 mb-3">
        <Col sm={4} className="mb-3">
          <div className="skeleton-loader mb-2" style={skeletonStyle('150px')}></div>
          <div className="skeleton-loader" style={skeletonStyle('100%', '38px')}></div>
        </Col>
        <Col sm={4} className="mb-3">
          <div className="skeleton-loader mb-2" style={skeletonStyle('150px')}></div>
          <div className="skeleton-loader" style={skeletonStyle('100%', '38px')}></div>
        </Col>
      </Row>

      <div>
        {/* Tabs skeleton */}
        <div className="skeleton-loader mb-3" style={skeletonStyle('100%', '40px')}></div>

        <div className="mt-5">
          {/* Email template editor skeleton */}
          <Row className="mb-3">
            <Col sm={3}>
              {/* Dynamic keys section */}
              <div className="skeleton-loader mb-2" style={skeletonStyle('150px')}></div>
              <div className="skeleton-loader mb-2" style={skeletonStyle('100%', '30px')}></div>
              <div className="skeleton-loader mb-2" style={skeletonStyle('100%', '30px')}></div>
              <div className="skeleton-loader mb-2" style={skeletonStyle('100%', '30px')}></div>
              <div className="skeleton-loader mb-2" style={skeletonStyle('100%', '30px')}></div>
              <div className="skeleton-loader mb-2" style={skeletonStyle('100%', '30px')}></div>
            </Col>
            <Col sm={9}>
              {/* Email editor section */}
              <div className="skeleton-loader mb-3" style={skeletonStyle('150px')}></div>
              <div className="skeleton-loader" style={skeletonStyle('100%', '600px')}></div>

              {/* Action buttons */}
              <Row className="mt-4">
                <Col className="d-flex justify-content-end">
                  <div className="skeleton-loader me-2" style={skeletonStyle('100px', '38px')}></div>
                  <div className="skeleton-loader me-2" style={skeletonStyle('100px', '38px')}></div>
                  <div className="skeleton-loader" style={skeletonStyle('100px', '38px')}></div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  )
}

// Title Skeleton Component
const TitleSkeleton = () => (
  <div className="d-flex align-items-center">
    <h2 className="title-text">
      <div
        className="skeleton-loader"
        style={{
          width: '250px',
          height: '32px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          animation: 'pulse 1.5s infinite ease-in-out'
        }}
      ></div>
    </h2>
  </div>
)

export default ({ isEdit }) => {
  const {
    emailTemplate,
    loading,
    updateTemplate,
    tenantsList,
    createTemplate,
    emailTemplateId,
    getDynamicKeysStart,
    dynamicKeys,
    dispatch,
    template,
    setTemplate,
    galleryModal,
    setGalleryModal,
    navigate,
    setIsTestTemplateModalVisible,
    isTestTemplateModalVisible,
    testEmailTemplateHandler,
    testEmail,
    setTestEmail,
    testTemplateLoading,
    isHidden,
    selectedTab,
    setSelectedTab,
    languages,
    deleteEmailTemplate,
    show,
    setShow,
    handleDeleteYes,
    emailTypes,
    resetEmail
  } = useEditEmailTemplate({ isEdit })

  useEffect(() => {
    if (isEdit && emailTemplate?.tenantId) {
      dispatch(getTenantLanguagesStart({ isTenant: true, tenantId: emailTemplate?.tenantId }))
    }
  }, [emailTemplate?.tenantId])

  return (
    <>
      {loading ? (
        <>
          <div className='section-header-wrap mt-n3 mx-n3'>
            <Row>
              <Col sm={8}>
                <TitleSkeleton />
              </Col>
              <Col className='d-flex justify-content-end align-items-center'>
                <div className="skeleton-loader" style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite ease-in-out'
                }}></div>
              </Col>
              <Col className="text-end">
                <NotificationIconContainer />
              </Col>
            </Row>
          </div>
          <EmailTemplateFormSkeleton />
        </>
      ) : (
        <>
          <div className='section-header-wrap mt-n3 mx-n3'>
            <Row className='align-items-center'>
              <Col sm={8} className='backbtn'>
              <PageBackButton />
                <h2 className='title-text'>{`Email Template${isEdit ? `: ${emailTemplate?.label}` : ''}`}</h2>
              </Col>
              <Col className='d-flex justify-content-end align-items-center'>
                <Trigger message='Gallery'>
                  <Button
                    hidden={isHidden({ module: { key: 'ImageGallery', value: 'R' } })}
                    onClick={() => setGalleryModal(true)}
                    variant='btn btn-primary'
                  >
                    <FontAwesomeIcon icon={faImages} />
                  </Button>
                </Trigger>
              </Col>
              <Col xs={1} className="text-end">
                <NotificationIconContainer />
              </Col>
            </Row>
          </div>

          <div className='FormsBg'>
            <Formik
              enableReinitialize={emailTemplate}
              initialValues={{
                label: emailTemplate?.label || '',
                type: emailTemplate?.type || 0,
              }}
              validationSchema={emailTemplateSchema}
              onSubmit={(formValues) => {
                isEdit
                  ? updateTemplate({
                    data: {
                      ...formValues,
                      emailTemplateId,
                      type: parseInt(formValues.type),
                      templateCode: template,
                      language: selectedTab
                    }
                  })
                  : (selectedTab !== 'EN'
                    ? updateTemplate({
                      data: {
                        ...formValues,
                        emailTemplateId: emailTemplate?.emailTemplateId,
                        type: parseInt(formValues.type),
                        templateCode: template,
                        language: selectedTab,
                        create: true
                      }
                    })
                    : createTemplate({
                      data: {
                        ...formValues,
                        type: parseInt(formValues.type),
                        templateCode: template,
                        language: selectedTab
                      }
                    })
                  )
              }}
            >
              {({
                touched,
                errors,
                values,
                handleChange,
                handleSubmit,
                handleBlur,
                setFieldValue
              }) => (
                <BForm>
                  <Row className=' mt-3 mb-3'>
                    <Col sm={4} className='mb-3'>
                      <label
                        htmlFor='label'
                        className={
                          touched.label && errors.label ? 'text-danger' : ''
                        }
                      >
                        Template Label<span className='text-danger'> *</span>
                      </label>
                      <InputGroup
                        className={
                          touched.label && errors.label
                            ? 'border border-danger'
                            : ''
                        }
                      >
                        <BForm.Control
                          name='label'
                          placeholder='Enter Promotion Title'
                          value={values.label}
                          onInput={handleChange}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      </InputGroup>
                      <ErrorMessage
                        component='div'
                        name='label'
                        className='text-danger'
                      />
                    </Col>
                    <Col sm={4} className='mb-3'>
                      <label
                        htmlFor='type'
                        className={
                          touched.type && errors.type ? 'text-danger' : ''
                        }
                      >
                        Template Type<span className='text-danger'> *</span>
                      </label>
                      <InputGroup
                        className={
                          touched.type && errors.type
                            ? 'border border-danger'
                            : ''
                        }
                      >
                        <BForm.Select
                          name='type'
                          onInput={handleChange}
                          onBlur={handleBlur}
                          disabled={isEdit}
                          onChange={(e) => {
                            dispatch(
                              getDynamicKeysStart({
                                type: e.target.value,
                                isTenant: true,
                                emailTypes
                              })
                            )
                            handleChange(e)
                            emailTemplate && setFieldValue('label', '')
                            emailTemplate && resetEmail()
                            setSelectedTab('EN')
                          }}
                          value={values.type}
                        >
                          <option value='' disabled key=''>
                            Select Template Type
                          </option>
                          {emailTypes &&
                            Object.keys(emailTypes?.emailTypes?.INT_TO_VALUE).map((item) => {
                              return (
                                <option
                                  key={`type ${item}`}
                                  value={item}
                                >
                                  {emailTypes?.emailTypes?.INT_TO_VALUE?.[item]}
                                </option>
                              )
                            })}
                        </BForm.Select>
                      </InputGroup>
                      <ErrorMessage
                        component='div'
                        name='type'
                        className='text-danger'
                      />
                    </Col>
                  </Row>
                  <div>
                    {/* <Tabs
                      activeKey={selectedTab}
                      onSelect={(tab) => {
                        if (isEdit && emailTemplate?.templateCode?.EN !== undefined) {
                          setSelectedTab(tab)
                        } else {
                          toast('You must enter data for English language and Submit before switching to another language ', 'error')
                        }
                      }}
                      className='nav-light'
                    >
                      <Tab
                        eventKey='EN'
                        title={
                          <Trigger message='English'>
                            <span>EN</span>
                          </Trigger>
                        }
                        tabClassName={selectedTab !== 'EN' ? emailTemplate?.templateCode?.EN !== undefined ? 'email' : '' : 'email-active'}
                      > */}
                    <div className='mt-5'>
                      <EditEmailTemplate
                        dynamicKeys={dynamicKeys}
                        isHidden={isHidden}
                        setGalleryModal={setGalleryModal}
                        emailTemplate={emailTemplate}
                        setIsTestTemplateModalVisible={setIsTestTemplateModalVisible}
                        isTestTemplateModalVisible={isTestTemplateModalVisible}
                        updateTemplate={updateTemplate}
                        galleryModal={galleryModal}
                        testTemplateLoading={testTemplateLoading}
                        testEmailTemplateHandler={testEmailTemplateHandler}
                        testEmail={testEmail}
                        setTestEmail={setTestEmail}
                        selectedTab={selectedTab}
                        deleteEmailTemplate={deleteEmailTemplate}
                        handleSubmit={handleSubmit}
                        setTemp={setTemplate}
                        create={!isEdit}
                        values={values}
                        label={emailTemplate?.label}
                        isTenant
                      />
                    </div>
                    {/* </Tab> */}
                    {/* </Tabs> */}
                  </div>
                </BForm>
              )}
            </Formik>
          </div>
        </>
      )}

      {show &&
        <DeleteConfirmationModal
          handleDeleteYes={handleDeleteYes}
          setDeleteModalShow={setShow}
          deleteModalShow={show}
        />}

      {galleryModal &&
        <GalleryModal
          galleryModal={galleryModal}
          setGalleryModal={setGalleryModal}
          navigate={navigate}
          isTenant
        />}
    </>
  )
}
